import { useState, useEffect, MouseEvent, DragEvent, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { CardForm } from '@pages/QuoteDetails/sidebarItems/notes/noteMessageForm/CardForm';
import * as styles from '@components/drawerItem/DrawerItem.styles';
import { Note, NoteType } from '@models/note';
import { searchNoteParams, useSearchNotes } from '@services/note';
import FileDropWrapper from '@utils/FileDropWrapper';
import { isDataTransfer } from '@utils/fileUtils';
import { Dropzone } from '@components/drawerItem';
import Loading from '@components/Loading';
import { EmptyMessagesList } from '@pages/QuoteDetails/sidebarItems/messages/EmptyMessagesList';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';
import { FileUpload, InfoCircle, Thumbtack } from '@assets/svgs';
import { NotesList } from '@components/notesDialog/components/NotesList';
import { PinnedNotesBar } from '@components/notesDialog/components/PinnedNotesBar';
import { GET_ALL_PAGE_LIMIT } from '@services/config';

export const emptyMessageListItems = [
  {
    icon: <InfoCircle />,
    text: 'Notes are only visible to internal LIO users and are part of the permanent record.',
  },
  {
    icon: <Thumbtack />,
    text: 'You can pin notes to the top of the notes feed to make them prominent to other team members or for easy access.',
  },
  {
    icon: <FileUpload />,
    text: 'You can attach images, videos, and other files by dragging and dropping them here or by browsing your files manually.',
  },
];

export const NoteDrawerItem = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const [searchbarText, setSearchbarText] = useState<string>('');

  const { searchNotes, isLoading, isFetchingNextPage, fetchNextPage } = useSearchNotes(
    {
      quoteId: quoteId as string,
      ...searchNoteParams,
    },
    searchbarText,
  );

  const { searchNotes: pinnedNotesQuery } = useSearchNotes(
    {
      quoteId: quoteId as string,
      ...searchNoteParams,
      isPinned: true,
      pageLimit: GET_ALL_PAGE_LIMIT,
      sort: '-pinnedAt,-createdAtOrReplacedAt',
    },
    searchbarText,
  );

  const [selectedToggle, setSelectedToggle] = useState<string>('toggleEditor');
  const [draggedFiles, setDraggedFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [isEditorDialogOpen, setIsEditorDialogOpen] = useState(false);
  const allNotes = searchNotes?.pages?.flatMap<Note>((page) => page.items);

  const pinnedNotes = useMemo(
    () => pinnedNotesQuery?.pages?.flatMap((p) => p.items).filter((n: any) => n.isPinned && !n.deletedAt) ?? [],
    [pinnedNotesQuery],
  );

  const debouncedSearchbarText = useDebouncedCallback((value) => {
    setSearchbarText(value);
  }, DEBOUNCE_DELAYS.TYPING);

  const handleOnChangeSearchbar = (newValue: string) => {
    debouncedSearchbarText(newValue?.trim());
  };

  const handleOnCleanSearchbarText = () => {
    setSearchbarText('');
  };

  useEffect(() => {
    if (selectedToggle === 'toggleEditor') {
      handleOnCleanSearchbarText();
    }
  }, [selectedToggle]);

  const handleOnChangeToggle = (event: MouseEvent<HTMLElement>, newValue: string) => {
    if (newValue !== null) {
      setSelectedToggle(newValue);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const newDraggedFiles: File[] = [];
    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i += 1) {
        if (e.dataTransfer.items[i].kind === 'file') {
          const file = e.dataTransfer.items[i].getAsFile();
          if (file !== null) {
            newDraggedFiles.push(file);
          }
        }
      }
      setDraggedFiles(newDraggedFiles);
    }
  };
  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDataTransfer(e)) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const resetDraggedFiles = () => {
    setDraggedFiles([]);
    setDragging(false);
  };

  const totalNotes = searchNotes?.pages?.[0]?.pagination?.total || 0;
  const loadedItems = allNotes?.length;
  const hasMoreToLoad = totalNotes > (loadedItems || 0);

  return (
    <>
      <Box
        id='notes-sidebar'
        sx={{
          backgroundColor: 'drawerNavigation.light',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <FileDropWrapper
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          disabled={isEditorDialogOpen}
          sx={{
            ...styles.parentContainer,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: 'drawerNavigation.light',
          }}
        >
          {dragging ? (
            <Dropzone
              sxProps={{
                dropzoneContainerSx: { marginTop: 0 },
              }}
            />
          ) : (
            <>
              {/* Fixed section - CardForm */}
              <Box
                sx={{
                  flexShrink: 0,
                  zIndex: 1,
                  backgroundColor: 'drawerNavigation.light',
                }}
              >
                <CardForm
                  type={NoteType.Note}
                  selectedToggle={selectedToggle}
                  onChangeToggle={handleOnChangeToggle}
                  searchbarText={searchbarText}
                  onChangeSearchbar={handleOnChangeSearchbar}
                  onCleanSearchbar={handleOnCleanSearchbarText}
                  draggedFiles={draggedFiles}
                  resetDraggedFiles={resetDraggedFiles}
                  onDialogOpenChange={setIsEditorDialogOpen}
                />
              </Box>
              {isLoading ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'drawerNavigation.light',
                  }}
                >
                  <Loading />
                </Box>
              ) : selectedToggle === 'toggleEditor' ? (
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    backgroundColor: 'drawerNavigation.light',
                    // Firefox-specific fixes
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                    // Webkit scrollbar styling (Chrome, Safari, Edge)
                    marginRight: '4px',
                    '&::-webkit-scrollbar': {
                      width: '0px',
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'drawerNavigation.light',
                    },
                    // Show scrollbar on hover for Chrome
                    '&:hover::-webkit-scrollbar': {
                      width: '8px',
                    },
                  }}
                >
                  {allNotes && allNotes.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        width: '100%',
                        minHeight: 0,
                        height: '100%',
                        flex: 1,
                        overflow: 'auto',
                      }}
                    >
                      <PinnedNotesBar pinnedNotes={pinnedNotes || []} />
                      <NotesList
                        notes={allNotes}
                        searchBarText={undefined}
                        fetchMore={fetchNextPage}
                        isFetchingMore={isFetchingNextPage}
                        hasMore={hasMoreToLoad}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        paddingBottom: '8vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'drawerNavigation.light',
                      }}
                    >
                      <EmptyMessagesList
                        title='Create a Note'
                        subtitle='If you want to save notes for yourself or the LIO team, enter them here.'
                        items={emptyMessageListItems}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    backgroundColor: 'drawerNavigation.light',
                    // Firefox-specific fixes
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                    // Webkit scrollbar styling (Chrome, Safari, Edge)
                    '&::-webkit-scrollbar': {
                      width: '0px',
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'drawerNavigation.light',
                    },
                    // Show scrollbar on hover for Chrome
                    '&:hover::-webkit-scrollbar': {
                      width: '8px',
                    },
                  }}
                >
                  {allNotes && allNotes.length > 0 ? (
                    <NotesList notes={allNotes} searchBarText={searchbarText} />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                        padding: '16px',
                      }}
                    >
                      <Typography variant='body1' color='text.secondary' align='center'>
                        There are no notes matching your search
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </FileDropWrapper>
      </Box>
    </>
  );
};
